import { useRef, useState, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from '../../../ui/button';
import { useUploadCuratedIcon } from '../../../../api/curated-integrations.api';
import { toast } from 'sonner';

const ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'];
const MAX_FILE_SIZE_BYTES = 500 * 1024; // 500 KB

interface IconUploadFieldProps {
  /** UUID of the integration (needed for the upload endpoint). */
  integrationId: string;
  /** Current icon URL to show as preview (from the integration data). */
  currentIconUrl?: string;
  /** Called after a successful upload with the new icon URL. */
  onUploaded: (newIconUrl: string) => void;
}

/**
 * Drag-and-drop / file-input field for uploading a curated integration icon.
 * Validates MIME type and file size client-side before uploading.
 * Shows the current (or newly uploaded) icon as a preview.
 */
export function IconUploadField({
  integrationId,
  currentIconUrl,
  onUploaded,
}: IconUploadFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | undefined>(currentIconUrl);
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string | undefined>();
  const { mutateAsync, isLoading } = useUploadCuratedIcon();

  const processFile = useCallback(
    async (file: File) => {
      setValidationError(undefined);

      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        setValidationError('Nur PNG, JPEG und WebP sind erlaubt.');
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setValidationError(`Datei zu groß. Maximal 500 KB erlaubt (aktuell: ${(file.size / 1024).toFixed(0)} KB).`);
        return;
      }

      // Local preview
      const objectUrl = URL.createObjectURL(file);
      setPreview(objectUrl);

      try {
        const updated = await mutateAsync({ id: integrationId, file });
        if (updated.iconUrl) {
          onUploaded(updated.iconUrl);
          // Replace temporary object-URL with the CDN URL
          URL.revokeObjectURL(objectUrl);
          setPreview(updated.iconUrl);
        }
        toast.success('Icon hochgeladen.');
      } catch (err: unknown) {
        URL.revokeObjectURL(objectUrl);
        setPreview(currentIconUrl);
        const message = err instanceof Error ? err.message : 'Upload fehlgeschlagen.';
        toast.error(message);
      }
    },
    [integrationId, currentIconUrl, mutateAsync, onUploaded],
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      void processFile(file);
    }
    // Reset input so the same file can be re-selected
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      void processFile(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleRemove = () => {
    setPreview(undefined);
    setValidationError(undefined);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      inputRef.current?.click();
    }
  };

  return (
    <div className="space-y-2">
      {/* Drop zone */}
      <div
        role="button"
        tabIndex={0}
        aria-label="Icon hochladen: Klicken oder Datei hierher ziehen"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onKeyDown={handleKeyDown}
        onClick={() => inputRef.current?.click()}
        className={[
          'relative flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed cursor-pointer transition-colors',
          'min-h-[120px] p-4',
          isDragging
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 hover:border-gray-400 bg-gray-50 hover:bg-gray-100',
        ].join(' ')}
      >
        {isLoading ? (
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" aria-label="Wird hochgeladen…" />
        ) : preview ? (
          <div className="flex flex-col items-center gap-2">
            <img
              src={preview}
              alt="Integrationsicon Vorschau"
              className="w-16 h-16 object-contain rounded-lg border border-gray-200 bg-white"
            />
            <span className="text-xs text-gray-500">Klicken zum Ersetzen</span>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center">
              <ImageIcon className="w-6 h-6 text-gray-400" aria-hidden="true" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-gray-700">
                <Upload className="inline w-3.5 h-3.5 mr-1 text-blue-500" aria-hidden="true" />
                Icon hochladen
              </p>
              <p className="text-xs text-gray-400 mt-0.5">PNG, JPEG oder WebP, max. 500 KB</p>
            </div>
          </>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        onChange={handleFileChange}
        className="sr-only"
        aria-hidden="true"
        tabIndex={-1}
      />

      {/* Remove button */}
      {preview && !isLoading && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleRemove}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
          aria-label="Icon entfernen"
        >
          <X className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
          Icon entfernen
        </Button>
      )}

      {/* Validation error */}
      {validationError && (
        <p role="alert" className="text-xs text-red-600">
          {validationError}
        </p>
      )}
    </div>
  );
}
